import type { DocumentBlock, DocumentContent } from '@morph/spec';
import { parseDocumentContent, serializeDocumentContent, CURRENT_DOCUMENT_VERSION } from '@morph/spec';

export type DocumentBlockIdFactory = (ordinal: number) => string;

/**
 * Lift a legacy one-value body into canonical paragraph blocks.
 *
 * Newlines are retained inside their paragraph rather than normalized. Blank
 * lines delimit blocks, which makes the reverse operation byte-for-byte
 * lossless without smuggling markup into canonical content.
 */
export function migrateBodyToBlocks(body: string, idFor: DocumentBlockIdFactory): DocumentContent {
  const separators: string[] = [];
  const chunks: string[] = [];
  let start = 0;
  for (const match of body.matchAll(/(?:\r?\n){2}/g)) {
    chunks.push(body.slice(start, match.index));
    separators.push(match[0]);
    start = match.index + match[0].length;
  }
  chunks.push(body.slice(start));
  const blocks = chunks.map((text, index) => ({
    id: idFor(index), kind: 'paragraph' as const, text,
  }));
  // Exact separators are content migration metadata, not layout or markup.
  // It lives in the transport envelope until every legacy body is migrated.
  /* DERIVED, not the literal 1 it was — task `0200`. This migration produces
     paragraphs only, so the answer is 1 today and the behaviour is unchanged.
     What the literal could not survive is a migration that one day emits a
     kind version 1 does not admit: it would stamp a version whose contract the
     content breaks, which is the exact defect `0200` was filed for, arriving
     through the one writer nobody would think to check. */
  /* CURRENT, not the lowest that admits paragraphs — decision `0075`, and
     the same ruling as `persistDocument`'s. Migrating a legacy body IS a
     current write, so its envelope carries the contract this build writes. */
  return { version: CURRENT_DOCUMENT_VERSION, roots: blocks.map((b) => b.id), blocks, legacySeparators: separators };
}

/** Serialize through the spec boundary so import/export never depends on DOM. */
export const serializeMigratedDocument = (content: DocumentContent): string =>
  serializeDocumentContent(content);

export const parseMigratedDocument = (serialized: string): DocumentContent =>
  parseDocumentContent(serialized);

/** Lossless path back to the old body slot during the compatibility window. */
export function blocksToLegacyBody(content: DocumentContent): string {
  const byId = new Map(content.blocks.map((block) => [block.id, block]));
  const separators = content.legacySeparators;
  return content.roots.map((id, index) => {
    const block = byId.get(id);
    if (block?.kind !== 'paragraph') throw new Error('document cannot be represented by one legacy body value');
    return `${index === 0 ? '' : separators?.[index - 1] ?? '\n\n'}${block.text ?? ''}`;
  }).join('');
}


/**
 * The compatibility projection decision 0028 specifies, for a lens that asks a
 * migrated note for `body`.
 *
 * NOT `blocksToLegacyBody`, and the difference is the point. That one is the
 * byte-fidelity writer: it round-trips a paragraph-only migrated tree exactly
 * and THROWS on anything it cannot represent, which is correct for a
 * compatibility WRITE and useless for a read. This one always answers, because
 * a lens asking for `body` needs prose rather than an exception.
 *
 * > *"A lens that is not document-aware may display that projection, use it for
 * > pool vocabulary, indexing, excerpts and footnote derivation, but it must
 * > expose it as read-only."*
 *
 * It is deliberately LOSSY and deliberately not an import format. Decision 0028
 * again: *"not canonical storage, an import format, or permission to
 * reconstruct blocks by reparsing plain text."* Structure that cannot survive
 * the flattening is exactly why writing the projection back is refused.
 *
 * `label` resolves an embed or media target to something a person can read, and
 * the CALLER supplies it — from the same coherent read that produced `content`,
 * which is what makes the whole projection *"computed from one coherent
 * document read"* rather than a scatter of live lookups. A target the caller
 * cannot resolve gets an explicit placeholder, never a silent omission: a
 * document that quietly loses its embeds when the reader lacks access reads as
 * a shorter document rather than a restricted one.
 */
export interface DocumentBodyProjection {
  /** Resolve an embed or media target to a human-readable label. */
  readonly label?: (target: string) => string | undefined;
  /** Between root blocks. Deterministic, per the decision. */
  readonly separator?: string;
  /** Before each list item. Deterministic, per the decision. */
  readonly bullet?: string;
}

export function projectDocumentBody(
  content: DocumentContent, options: DocumentBodyProjection = {},
): string {
  const separator = options.separator ?? '\n\n';
  const bullet = options.bullet ?? '- ';
  const byId = new Map(content.blocks.map((block) => [block.id, block]));

  // A paragraph-only tree that still carries its migration envelope round-trips
  // BYTE FOR BYTE, because that is what the envelope is for and the decision
  // names it: "when a paragraph-only migrated tree still carries valid
  // legacySeparators, `blocksToLegacyBody` preserves the original bytes".
  if (content.legacySeparators) {
    try { return blocksToLegacyBody(content); } catch { /* structure it cannot hold */ }
  }

  const piece = (id: string): string | undefined => {
    const block = byId.get(id);
    if (!block) return undefined;
    switch (block.kind) {
      case 'paragraph':
      case 'heading':
      // A QUOTE PROJECTS AS ITS BARE TEXT, deliberately, and it is listed here
      // rather than left to the default so the next reader can tell this was
      // decided. The legacy envelope is a plain Body slot, and a heading
      // already drops its own marker: only a list carries one, because its
      // items would otherwise run together on a single line, which makes the
      // bullet structural separation rather than markdown.
      case 'quote':
        return block.text ?? '';
      case 'list':
        return (block.children ?? [])
          .map((child) => byId.get(child))
          .filter((child): child is DocumentBlock => !!child)
          .map((child) => `${bullet}${child.text ?? ''}`)
          .join('\n');
      case 'list-item':
        // Reachable only if a list item is a ROOT, which the validator does not
        // produce. Answered rather than thrown: a projection that refuses is
        // the exception this function exists to avoid.
        return `${bullet}${block.text ?? ''}`;
      case 'embed':
      case 'media': {
        const named = block.target === undefined ? undefined : options.label?.(block.target);
        return named ?? (block.kind === 'embed' ? '[embedded object]' : '[media]');
      }
      default:
        return block.text ?? '';
    }
  };

  return content.roots
    .map(piece)
    .filter((text): text is string => text !== undefined)
    .join(separator);
}
